import { put, takeLeading } from 'redux-saga/effects';
import jwtDecode from 'jwt-decode';

import { LOCATIONS, storageKey, LOGIN_TYPE } from 'constants/index';

import userApi from 'api/userApi';
import authApi from 'api/authApi';

import {
  apiErrorHandler,
  removeSpacesWithTrim,
  showSuccessTrans,
} from 'utils/index';
import axiosClient from 'utils/axios';

import {
  UPDATE_ME,
  UPDATE_MY_AVATAR,
  DELETE_MY_AVATAR,
  UPDATE_MY_PASSWORD,
  GET_ME,
  LOGOUT,
  GET_ME_SUCCESS,
  GET_ME_FAILED,
  GET_PROFILE_EXTRANET,
  GET_PROFILE_EXTRANET_SUCCESS,
  GET_PROFILE_EXTRANET_FAILED,
  REFRESH_TOKEN_SUCCESS,
} from './actionTypes';

import {
  actionUpdateMeSuccess,
  actionUpdateMeFailed,

  actionUpdateMyAvatarSuccess,
  actionUpdateMyAvatarFailed,

  actionDeleteMyAvatarSuccess,
  actionDeleteMyAvatarFailed,

  actionUpdateMyPasswordSuccess,
  actionUpdateMyPasswordFailed,
} from './actions';

function* getMyUserProfile({ payload }) {
  try {
    const token = localStorage.getItem(storageKey.AUTHENTICATE_TOKEN);
    let isIntranet = true;

    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded && typeof decoded.isIntranet === 'boolean') {
          isIntranet = decoded.isIntranet;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to decode token in profile saga:', e);
      }
    } else {
      const loginType = localStorage.getItem(storageKey.LOGIN_TYPE);
      if (loginType === LOGIN_TYPE.EXTRANET) {
        isIntranet = false;
      }
    }

    const response = isIntranet
      ? yield userApi.getProfileIntranet()
      : yield userApi.getProfileExtranet();

    yield put({
      type: GET_ME_SUCCESS,
      payload: {
        ...response.payload,
        isIntranet,
      },
    });
  } catch (error) {
    apiErrorHandler(error);

    localStorage.removeItem(storageKey.AUTHENTICATE_TOKEN);
    payload?.history?.push(LOCATIONS.LOGIN);

    yield put({ type: GET_ME_FAILED });
  }
}

function* getProfileExtranet({ payload }) {
  try {
    const response = yield userApi.getProfileExtranet();

    yield put({
      type: GET_PROFILE_EXTRANET_SUCCESS,
      payload: {
        ...response.payload,
        isIntranet: false,
      },
    });
  } catch (error) {
    apiErrorHandler(error);

    localStorage.removeItem(storageKey.AUTHENTICATE_TOKEN);
    payload?.history?.push(LOCATIONS.LOGIN);

    yield put({ type: GET_PROFILE_EXTRANET_FAILED });
  }
}

function* resetToken({ payload }) {
  try {
    const { refreshAccessToken: refreshToken } = payload;

    const response = yield authApi.onRefreshToken(refreshToken);

    localStorage.setItem(storageKey.AUTHENTICATE_TOKEN, response.token);

    axiosClient.defaults.headers.Authorization = response.token;
  } catch (error) {
    apiErrorHandler(error);
  }
}

function* updateMe({ payload }) {
  try {
    const userData = payload;

    const newData = removeSpacesWithTrim(userData);

    const response = yield userApi.updateMe(newData);

    showSuccessTrans({ type: 'type.UPDATE' });

    yield put(actionUpdateMeSuccess(response.payload));
  } catch (error) {
    let errors;

    if (error.response?.data?.errors?.length) {
      errors = error.response?.data?.errors;
    } else {
      apiErrorHandler(error);
    }
    yield put(actionUpdateMeFailed(errors));
  }
}

function* updateMyAvatar({ payload }) {
  try {
    const { avatar, callback } = payload;
    const response = yield userApi.uploadMyAvatar({ avatar });

    showSuccessTrans({ type: 'type.UPDATE' });

    yield put(actionUpdateMyAvatarSuccess(response.payload));
    callback();
  } catch (error) {
    apiErrorHandler(error);

    yield put(actionUpdateMyAvatarFailed());
  }
}

function* deleteMyAvatar() {
  try {
    yield userApi.deleteMyAvatar();

    yield put(actionDeleteMyAvatarSuccess());
    showSuccessTrans({ type: 'type.DELETE' });
  } catch (error) {
    apiErrorHandler(error);
    yield put(actionDeleteMyAvatarFailed());
  }
}

function* updateMyPassword({ payload }) {
  try {
    const { userPwd, callback } = payload;

    yield userApi.updateMyPassword({ userPwd });

    showSuccessTrans({ name: 'profile.change_password' });

    yield put(actionUpdateMyPasswordSuccess());

    callback();
  } catch (error) {
    let errors;

    if (error.response?.data?.errors?.length) {
      errors = error.response.data.errors;
    } else {
      apiErrorHandler(error);
    }

    yield put(actionUpdateMyPasswordFailed(errors));
  }
}

// eslint-disable-next-line require-yield
function* logout({ payload }) {
  try {
    const { history } = payload;

    localStorage.removeItem(storageKey.AUTHENTICATE_TOKEN);
    localStorage.removeItem('place');

    history.push(LOCATIONS.LOGIN);
  } catch (error) {
    apiErrorHandler(error);
  }
}

export default function* UserProfileSaga() {
  yield takeLeading(GET_ME, getMyUserProfile);
  yield takeLeading(GET_PROFILE_EXTRANET, getProfileExtranet);
  yield takeLeading(UPDATE_ME, updateMe);
  yield takeLeading(UPDATE_MY_AVATAR, updateMyAvatar);
  yield takeLeading(UPDATE_MY_PASSWORD, updateMyPassword);
  yield takeLeading(DELETE_MY_AVATAR, deleteMyAvatar);
  yield takeLeading(REFRESH_TOKEN_SUCCESS, resetToken);
  yield takeLeading(LOGOUT, logout);
}
